import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Container,
  Typography,
  Box,
  Grid,
  Tab,
  Tabs,
  CircularProgress,
  Alert,
  Button,
  Chip,
  Avatar,
  Divider,
} from '@mui/material';
import Iconify from '../components/iconify';
import { useUserDetails } from '../hooks/useUserDetails';
import EntriesTab from '../components/users/EntriesTab';
import TicketsTab from '../components/users/TicketsTab';
import PrizeDrawsTab from '../components/users/PrizeDrawsTab';

// Stat Card Component with gradient
function StatCard({ label, value, color = '#8B5CF6', icon }) {
  return (
    <Card
      sx={{
        p: 3,
        height: '100%',
        borderRadius: 3,
        background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
        border: `1px solid ${color}20`,
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 12px 24px ${color}20`,
        },
      }}
    >
      {icon && (
        <Box sx={{ mb: 2, color, opacity: 0.8 }}>
          <Iconify icon={icon} width={32} height={32} />
        </Box>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontWeight: 500 }}>
        {label}
      </Typography>
      <Typography variant="h4" sx={{ color, fontWeight: 700 }}>
        {value}
      </Typography>
    </Card>
  );
}

// Info Field Component with better styling
function InfoField({ label, value, icon }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
        {icon && (
          <Iconify 
            icon={icon} 
            width={14} 
            height={14} 
            sx={{ mr: 0.75, color: 'text.secondary' }} 
          />
        )}
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.7rem' }}>
          {label}
        </Typography>
      </Box>
      <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
        {value || '-'}
      </Typography>
    </Box>
  );
}

export default function UserDetailsPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user, loading, error, fetchUserDetails } = useUserDetails();
  const [currentTab, setCurrentTab] = useState(0);

  useEffect(() => {
    if (userId) {
      fetchUserDetails(userId);
    }
  }, [userId, fetchUserDetails]);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  if (loading && !user) {
    return (
      <Container sx={{ py: 10 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error && !user) {
    return (
      <Container sx={{ py: 5 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!user) {
    return (
      <Container sx={{ py: 5 }}>
        <Alert severity="warning">User not found</Alert>
      </Container>
    );
  }

  const stats = user.stats || {};

  return (
    <>
      <Helmet>
        <title>{user.username} | User Details</title>
      </Helmet>

      <Container maxWidth="xl">
        {/* Header Section */}
        <Box sx={{ mb: 4 }}>
          <Button
            startIcon={<Iconify icon="eva:arrow-back-fill" />}
            onClick={() => navigate('/dashboard/users')}
            sx={{ 
              mb: 3,
              color: 'text.secondary',
              '&:hover': { bgcolor: 'action.hover' }
            }}
          >
            Back to Users
          </Button>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              sx={{ 
                width: 64, 
                height: 64, 
                bgcolor: '#8B5CF6',
                fontSize: '1.5rem',
                fontWeight: 700
              }}
            >
              {user.username?.charAt(0).toUpperCase()}
            </Avatar>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                {user.username}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip 
                  label={`${user.tickets || '-'} Tickets`}
                  size="small"
                  sx={{ bgcolor: '#8B5CF620', color: '#8B5CF6', fontWeight: 600 }}
                />
                <Chip 
                  label={`${user.current_streak || '-'} Day Streak 🔥`}
                  size="small"
                  sx={{ bgcolor: '#10B98120', color: '#10B981', fontWeight: 600 }}
                />
                {user.bet99_league && (
                  <Chip 
                    label="Bet99 Member"
                    size="small"
                    icon={<Iconify icon="eva:checkmark-circle-2-fill" />}
                    sx={{ bgcolor: '#3B82F620', color: '#3B82F6', fontWeight: 600 }}
                  />
                )}
                {user.amazon_league && (
                  <Chip 
                    label="Amazon Member"
                    size="small"
                    icon={<Iconify icon="eva:checkmark-circle-2-fill" />}
                    sx={{ bgcolor: '#FF990020', color: '#FF9900', fontWeight: 600 }}
                  />
                )}
              </Box>
            </Box>
          </Box>
        </Box>

        {/* User Details Card */}
        <Card sx={{ 
          p: 4, 
          mb: 4, 
          borderRadius: 3, 
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          background: 'linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Box sx={{ 
              p: 1.5, 
              borderRadius: 2, 
              bgcolor: '#8B5CF610',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Iconify icon="eva:person-fill" width={24} sx={{ color: '#8B5CF6' }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Account Information
            </Typography>
          </Box>
          <Divider sx={{ mb: 4 }} />
          
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ 
                p: 2, 
                borderRadius: 2, 
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                height: '100%'
              }}>
                <InfoField 
                  label="User ID" 
                  value={user.user_id}
                  icon="eva:hash-outline"
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ 
                p: 2, 
                borderRadius: 2, 
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                height: '100%'
              }}>
                <InfoField 
                  label="User Service ID" 
                  value={user.service_user_id || '-'}
                  icon="eva:layers-outline"
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ 
                p: 2, 
                borderRadius: 2, 
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                height: '100%'
              }}>
                <InfoField 
                  label="SSO ID" 
                  value={user.sso_user_id || '-'}
                  icon="eva:shield-outline"
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ 
                p: 2, 
                borderRadius: 2, 
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                height: '100%'
              }}>
                <InfoField 
                  label="Username" 
                  value={user.username}
                  icon="eva:person-outline"
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ 
                p: 2, 
                borderRadius: 2, 
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                height: '100%'
              }}>
                <InfoField 
                  label="Location" 
                  value={user.location}
                  icon="eva:pin-outline"
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ 
                p: 2, 
                borderRadius: 2, 
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                height: '100%'
              }}>
                <InfoField 
                  label="Referral Code" 
                  value={user.referral_code}
                  icon="eva:gift-outline"
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ 
                p: 2, 
                borderRadius: 2, 
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                height: '100%'
              }}>
                <InfoField 
                  label="Ticket Balance" 
                  value={user.ticket_balance || '-'}
                  icon="eva:pricetags-outline"
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ 
                p: 2, 
                borderRadius: 2, 
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                height: '100%'
              }}>
                <InfoField 
                  label="Total Tickets Earned" 
                  value={user.total_tickets || '-'}
                  icon="eva:award-outline"
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ 
                p: 2, 
                borderRadius: 2, 
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                height: '100%'
              }}>
                <InfoField 
                  label="Current Streak" 
                  value={user.current_streak ? `${user.current_streak} days` : '-'}
                  icon="eva:flash-outline"
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ 
                p: 2, 
                borderRadius: 2, 
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                height: '100%'
              }}>
                <InfoField 
                  label="Total Referrals" 
                  value={user.referral_count || '-'}
                  icon="eva:people-outline"
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ 
                p: 2, 
                borderRadius: 2, 
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                height: '100%'
              }}>
                <InfoField 
                  label="Signup Date" 
                  value={user.created_at ? new Date(user.created_at).toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: 'short', 
                    day: 'numeric' 
                  }) : '-'}
                  icon="eva:calendar-outline"
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ 
                p: 2, 
                borderRadius: 2, 
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                height: '100%'
              }}>
                <InfoField 
                  label="Account Age" 
                  value={user.created_at ? `${Math.floor((new Date() - new Date(user.created_at)) / (1000 * 60 * 60 * 24))} days` : '-'}
                  icon="eva:clock-outline"
                />
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ my: 4 }} />

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>
              League Memberships
            </Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Box sx={{ 
                p: 2.5, 
                borderRadius: 2, 
                border: '2px solid',
                borderColor: user.bet99_opt_in ? '#8B5CF6' : 'divider',
                bgcolor: user.bet99_opt_in ? '#8B5CF608' : 'background.paper',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                transition: 'all 0.3s ease'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Iconify 
                    icon={user.bet99_opt_in ? "eva:checkmark-circle-2-fill" : "eva:close-circle-outline"} 
                    width={24} 
                    sx={{ color: user.bet99_opt_in ? '#8B5CF6' : 'text.disabled' }}
                  />
                  <Typography sx={{ fontWeight: 600, color: user.bet99_opt_in ? 'text.primary' : 'text.secondary' }}>
                    Bet99 Opt-in
                  </Typography>
                </Box>
                <Chip 
                  label={user.bet99_opt_in ? "Active" : "Inactive"}
                  size="small"
                  sx={{ 
                    bgcolor: user.bet99_opt_in ? '#8B5CF620' : 'action.hover',
                    color: user.bet99_opt_in ? '#8B5CF6' : 'text.secondary',
                    fontWeight: 600
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box sx={{ 
                p: 2.5, 
                borderRadius: 2, 
                border: '2px solid',
                borderColor: user.bet99_league ? '#3B82F6' : 'divider',
                bgcolor: user.bet99_league ? '#3B82F608' : 'background.paper',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                transition: 'all 0.3s ease'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Iconify 
                    icon={user.bet99_league ? "eva:checkmark-circle-2-fill" : "eva:close-circle-outline"} 
                    width={24} 
                    sx={{ color: user.bet99_league ? '#3B82F6' : 'text.disabled' }}
                  />
                  <Typography sx={{ fontWeight: 600, color: user.bet99_league ? 'text.primary' : 'text.secondary' }}>
                    Bet99 League
                  </Typography>
                </Box>
                <Chip 
                  label={user.bet99_league ? "Member" : "Not Joined"}
                  size="small"
                  sx={{ 
                    bgcolor: user.bet99_league ? '#3B82F620' : 'action.hover',
                    color: user.bet99_league ? '#3B82F6' : 'text.secondary',
                    fontWeight: 600
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box sx={{ 
                p: 2.5, 
                borderRadius: 2, 
                border: '2px solid',
                borderColor: user.amazon_league ? '#FF9900' : 'divider',
                bgcolor: user.amazon_league ? '#FF990008' : 'background.paper',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                transition: 'all 0.3s ease'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Iconify 
                    icon={user.amazon_league ? "eva:checkmark-circle-2-fill" : "eva:close-circle-outline"} 
                    width={24} 
                    sx={{ color: user.amazon_league ? '#FF9900' : 'text.disabled' }}
                  />
                  <Typography sx={{ fontWeight: 600, color: user.amazon_league ? 'text.primary' : 'text.secondary' }}>
                    Amazon League
                  </Typography>
                </Box>
                <Chip 
                  label={user.amazon_league ? "Member" : "Not Joined"}
                  size="small"
                  sx={{ 
                    bgcolor: user.amazon_league ? '#FF990020' : 'action.hover',
                    color: user.amazon_league ? '#FF9900' : 'text.secondary',
                    fontWeight: 600
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Card>

        {/* User Stats Grid */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Box sx={{ 
              p: 1.5, 
              borderRadius: 2, 
              bgcolor: '#10B98110',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Iconify icon="eva:bar-chart-fill" width={24} sx={{ color: '#10B981' }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Performance Statistics
            </Typography>
          </Box>
          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={6} md={2.4}>
              <StatCard 
                label="Total Entries" 
                value={stats.total_entries || '-'}
                color="#8B5CF6"
                icon="eva:file-text-fill"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <StatCard 
                label="Top Day Finish" 
                value={stats.top_day_finish || '-'}
                color="#3B82F6"
                icon="eva:sun-fill"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <StatCard 
                label="Top Week Finish" 
                value={stats.top_week_finish || '-'}
                color="#10B981"
                icon="eva:calendar-fill"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <StatCard 
                label="Top Month Finish" 
                value={stats.top_month_finish || '-'}
                color="#F59E0B"
                icon="eva:star-fill"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <StatCard 
                label="Top Period Rank" 
                value={stats.top_period_rank || '-'}
                color="#EC4899"
                icon="eva:trending-up-fill"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <StatCard 
                label="Total Tickets" 
                value={stats.total_tickets || '-'}
                color="#14B8A6"
                icon="eva:award-fill"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <StatCard 
                label="Total Prize Draw Entries" 
                value={stats.total_prize_draw_entries || '-'}
                color="#8B5CF6"
                icon="eva:gift-fill"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <StatCard 
                label="Total 7 Day Streaks" 
                value={stats.total_7_day_streaks || '-'}
                color="#EF4444"
                icon="eva:flash-fill"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <StatCard 
                label="Friends Leagues Joined" 
                value={stats.friends_leagues_joined || '-'}
                color="#6366F1"
                icon="eva:people-fill"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <StatCard 
                label="Friends Leagues Created" 
                value={stats.friends_leagues_created || '-'}
                color="#A855F7"
                icon="eva:plus-circle-fill"
              />
            </Grid>
          </Grid>
        </Box>

        {/* Tabs Section */}
        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
          <Box sx={{ 
            borderBottom: 1, 
            borderColor: 'divider',
            bgcolor: 'background.neutral'
          }}>
            <Tabs 
              value={currentTab} 
              onChange={handleTabChange}
              sx={{
                '& .MuiTab-root': {
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  textTransform: 'none',
                  minHeight: 64,
                },
                '& .Mui-selected': {
                  color: '#8B5CF6',
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#8B5CF6',
                  height: 3,
                },
              }}
            >
              <Tab 
                label="Entries" 
                icon={<Iconify icon="eva:list-fill" width={20} />}
                iconPosition="start"
              />
              <Tab 
                label="Tickets" 
                icon={<Iconify icon="eva:pricetags-fill" width={20} />}
                iconPosition="start"
              />
              <Tab 
                label="Prize Draws" 
                icon={<Iconify icon="eva:gift-fill" width={20} />}
                iconPosition="start"
              />
            </Tabs>
          </Box>

          <Box sx={{ p: 3 }}>
            {currentTab === 0 && <EntriesTab userId={userId} />}
            {currentTab === 1 && <TicketsTab userId={userId} />}
            {currentTab === 2 && <PrizeDrawsTab userId={userId} />}
          </Box>
        </Card>
      </Container>
    </>
  );
}
